import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { propertyAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Home,
  MapPin,
  Building,
  Edit,
  Trash2,
  DoorOpen,
  Camera,
} from 'lucide-react';
import ImageGallery from '../../components/ImageGallery';
import { useI18n } from '../../context/I18nContext';

const Properties = () => {
  const { t } = useI18n();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    property_type: 'residential',
    building_type: 'apartment',
    description: '',
    image_url: ''
  });

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      property_type: 'residential',
      building_type: 'apartment',
      description: '',
      image_url: ''
    });
    setEditingProperty(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProperty) {
        await propertyAPI.update(editingProperty.id, formData);
        toast.success('Property updated successfully');
      } else {
        await propertyAPI.create(formData);
        toast.success('Property created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      address: property.address,
      city: property.city,
      state: property.state,
      zip_code: property.zip_code,
      property_type: property.property_type,
      building_type: property.building_type || 'apartment',
      description: property.description || '',
      image_url: property.image_url || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      await propertyAPI.delete(id);
      toast.success('Property deleted');
      loadProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleImageUpload = async (propertyId, data) => {
    try {
      await propertyAPI.uploadImage(propertyId, data);
      toast.success('Image uploaded');
      loadProperties();
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleImageDelete = async (propertyId, imageId) => {
    try {
      await propertyAPI.deleteImage(propertyId, imageId);
      toast.success('Image deleted');
      loadProperties();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const defaultImages = [
    'https://images.unsplash.com/photo-1637747022660-12ce5ce4e420?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    'https://images.unsplash.com/photo-1680416124510-5eae1beca412?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    'https://images.unsplash.com/photo-1759092913686-5ce5f9da67fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
  ];

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('properties.title')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="landlord" title={t('properties.title')}>
      <div className="space-y-6" data-testid="properties-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground">{t('properties.subtitle')}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gold text-white hover:bg-gold-light" data-testid="add-property-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif">
                  {editingProperty ? 'Edit Property' : 'Add New Property'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Property Name</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-background border-[#e5e7eb]"
                    placeholder="Sunset Apartments"
                    required
                    data-testid="property-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Address</Label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="bg-background border-[#e5e7eb]"
                    placeholder="123 Main Street"
                    required
                    data-testid="property-address-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">City</Label>
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="bg-background border-[#e5e7eb]"
                      placeholder="Los Angeles"
                      required
                      data-testid="property-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">State</Label>
                    <Input
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="bg-background border-[#e5e7eb]"
                      placeholder="CA"
                      required
                      data-testid="property-state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">ZIP</Label>
                    <Input
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      className="bg-background border-[#e5e7eb]"
                      placeholder="90001"
                      required
                      data-testid="property-zip-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Property Type</Label>
                    <select
                      name="property_type"
                      value={formData.property_type}
                      onChange={handleChange}
                      className="w-full h-10 px-3 bg-background border border-[#e5e7eb] text-foreground rounded-lg"
                      data-testid="property-type-select"
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="mixed">Mixed Use</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Building Type</Label>
                    <select
                      name="building_type"
                      value={formData.building_type}
                      onChange={handleChange}
                      className="w-full h-10 px-3 bg-background border border-[#e5e7eb] text-foreground rounded-lg"
                      data-testid="building-type-select"
                    >
                      <option value="apartment">Apartment</option>
                      <option value="triplex">Triplex</option>
                      <option value="duplex">Duplex</option>
                      <option value="retail">Retail</option>
                      <option value="warehouse">Warehouse</option>
                      <option value="office">Office</option>
                      <option value="single_family">Single Family</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Description</Label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="bg-background border-[#e5e7eb]"
                    placeholder="Describe your property..."
                    data-testid="property-description-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-gold text-white hover:bg-gold-light" data-testid="save-property-btn">
                  {editingProperty ? 'Update Property' : 'Create Property'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Properties Grid */}
        {properties.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Home className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">{t('properties.noProperties')}</h3>
              <p className="text-muted-foreground mb-6">
                {t('properties.noPropertiesDesc')}
              </p>
              <Button onClick={() => setDialogOpen(true)} className="bg-gold text-white hover:bg-gold-light">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Property
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property, index) => (
              <Card 
                key={property.id} 
                className="bg-card border-border overflow-hidden hover:border-gold/30 transition-all duration-300 card-animate"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`property-card-${property.id}`}
              >
                <div 
                  className="h-40 bg-cover bg-center relative"
                  style={{ 
                    backgroundImage: `url(${property.image_url || defaultImages[index % defaultImages.length]})`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex gap-2">
                    <span className="px-3 py-1 bg-gold/90 text-white text-xs uppercase tracking-wider">
                      {property.property_type}
                    </span>
                    {property.building_type && (
                      <span className="px-3 py-1 bg-white/20 text-white text-xs uppercase tracking-wider backdrop-blur-sm">
                        {property.building_type}
                      </span>
                    )}
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="font-serif text-lg mb-2">{property.name}</h3>
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{property.city}, {property.state}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-4">
                    <DoorOpen className="w-4 h-4" />
                    <span>{property.unit_count || 0} Units</span>
                  </div>
                  <div className="flex gap-2 mb-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-[#e5e7eb] hover:border-gold"
                      onClick={() => handleEdit(property)}
                      data-testid={`edit-property-${property.id}`}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      onClick={() => handleDelete(property.id)}
                      data-testid={`delete-property-${property.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <ImageGallery
                    images={property.images || []}
                    onUpload={(data) => handleImageUpload(property.id, data)}
                    onDelete={(imgId) => handleImageDelete(property.id, imgId)}
                    editable
                    maxImages={6}
                    label="Property Photos"
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Properties;
